import { useState, useMemo, useEffect, useCallback } from 'react';
import { formatDateBR, toInputDate } from '../utils/dateUtils';
import AlertDialog from '../components/AlertDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  EyeIcon,
  UserPlusIcon,
  UserMinusIcon,
  ArrowsRightLeftIcon,
  CalendarDaysIcon,
  ArrowRightIcon,
  InformationCircleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { colaboradoresService, type CreateColaboradorDto, type UpdateColaboradorDto } from '../services/colaboradores.service';
import { cargosService, type Cargo } from '../services/cargos.service';
import { setoresService, type Setor } from '../services/setores.service';
import ModalAdmissaoRetroativa from '../components/Colaboradores/ModalAdmissaoRetroativa';
import ModalDesligamentoSemDemissional from '../components/Colaboradores/ModalDesligamentoSemDemissional';
import TransferirColaboradoresModal from '../components/Colaboradores/TransferirColaboradoresModal';

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
}
import { useEmpresa } from '../contexts/EmpresaContext';
import DataTable from '../components/DataTable/DataTable';
import PageContainer from '../components/PageContainer';
import PageHeader from '../components/PageHeader';
import type { Column } from '../types/data-table';
import type { FilterState } from '../types/data-table';
import { format } from 'date-fns';
import { formatCPF, formatPhone } from '../utils/formatters';
import { maskCPF, maskPhone, unmask } from '../utils/masks';
import { isValidCPF } from '../utils/validators';
import type { Colaborador } from '../services/colaboradores.service';
import ColaboradorForm from '../components/Forms/ColaboradorForm';
import { domainService } from '../services/domain.service';
import Toggle from '../components/Toggle';
import { useToast, ToastContainer } from '../components/Toast';

export default function Colaboradores() {
  const { empresa } = useEmpresa();
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingColaborador, setEditingColaborador] = useState<Colaborador | null>(null);
  const [creationStep, setCreationStep] = useState<'aviso_admissional' | 'formulario'>('aviso_admissional');

  // Estados dos Modais de Manutenção de Quadro / Autosserviço
  const [isRetroModalOpen, setIsRetroModalOpen] = useState(false);
  const [isDesligamentoModalOpen, setIsDesligamentoModalOpen] = useState(false);
  const [desligamentoTargetColab, setDesligamentoTargetColab] = useState<Colaborador | null>(null);

  // Estados de Transferência de Colaboradores entre Filiais
  const [selectedColaboradoresIds, setSelectedColaboradoresIds] = useState<number[]>([]);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [transferTargetColaboradores, setTransferTargetColaboradores] = useState<Colaborador[]>([]);

  // Consulta de Filiais do Grupo Econômico para habilitar/desabilitar transferências
  const { data: filiaisData } = useQuery({
    queryKey: ['colaboradores-filiais', empresa?.id],
    queryFn: () => colaboradoresService.getFiliais(),
    enabled: !!empresa?.id,
  });
  const filiaisDisponiveis = filiaisData?.filiais || [];
  const hasGrupoEconomicoFiliais = filiaisDisponiveis.length > 0;

  const handleOpenDesligamento = (colaborador?: Colaborador) => {
    setDesligamentoTargetColab(colaborador || null);
    setIsDesligamentoModalOpen(true);
  };
  const [filters, setFilters] = useState<FilterState>({
    enabled: 'all',
    search: '',
    dateCreatedFrom: '',
    dateCreatedTo: '',
    dateUpdatedFrom: '',
    dateUpdatedTo: '',
  });

  // Dialog States
  const [alertState, setAlertState] = useState<{ isOpen: boolean; title: string; message: string; variant: 'success' | 'error' | 'warning' | 'info' }>({
    isOpen: false,
    title: '',
    message: '',
    variant: 'info'
  });
  const [confirmState, setConfirmState] = useState<{ isOpen: boolean; title: string; message: string; onConfirm: () => void }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => { }
  });

  const showAlert = (title: string, message: string, variant: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setAlertState({ isOpen: true, title, message, variant });
  };

  const [formData, setFormData] = useState<CreateColaboradorDto>({
    nome: '',
    nome_social: '',
    pcd: false,
    enviar_esocial: true,
    regime_contratacao: '',
    estado_civil: '',
    cpf: '',
    rg: '',
    email: '',
    telefone: '',
    data_nascimento: '',
    data_admissao: '',
    data_demissao: '',
    sexo: '',
    formacao_academica_id: undefined,
    cargo_id: undefined,
    setor_id: undefined,
    cargo_nome: undefined,
    setor_nome: undefined,
    ultimo_periodico: '',
    tempo_periodico: undefined,
    tipo_id: undefined,
  });

  const apiFilters = useMemo(() => {
    const params: { page: number; limit: number; search?: string } = {
      page,
      limit,
    };

    if (filters.search) {
      params.search = filters.search;
    }

    return params;
  }, [page, limit, filters]);

  const { data: response, isLoading } = useQuery({
    queryKey: ['colaboradores', empresa?.id, apiFilters],
    queryFn: () => colaboradoresService.findAll(apiFilters),
    enabled: !!empresa?.id,
  });

  const { data: cargosData } = useQuery({
    queryKey: ['cargos', empresa?.id],
    queryFn: () => cargosService.findAll(1000),
    enabled: !!empresa?.id,
  });

  const { data: setoresData } = useQuery({
    queryKey: ['setores', empresa?.id],
    queryFn: () => setoresService.findAll(1000),
    enabled: !!empresa?.id,
  });

  const { data: formacoesData } = useQuery({
    queryKey: ['domain', 'academic-formation'],
    queryFn: () => domainService.getFormacoes(),
  });

  const { data: tiposContrato } = useQuery({
    queryKey: ['tipos-contrato'],
    queryFn: () => domainService.getContractTypes(),
  });

  // Query para buscar detalhes do colaborador quando editando
  const { data: colaboradorDetalhes, isLoading: isLoadingDetalhes } = useQuery({
    queryKey: ['colaborador', editingColaborador?.id],
    queryFn: () => colaboradoresService.findOne(editingColaborador!.id),
    enabled: !!editingColaborador?.id && isModalOpen,
  });

  // Efeito para preencher o formulário quando os dados chegarem da API
  useEffect(() => {
    if (colaboradorDetalhes && editingColaborador) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFormData((prev) => ({
        ...prev,
        id: colaboradorDetalhes.id,
        nome: colaboradorDetalhes.nome,
        nome_social: colaboradorDetalhes.nome_social || '',
        pcd: colaboradorDetalhes.pcd ?? false,
        enviar_esocial: colaboradorDetalhes.enviar_esocial ?? true,
        regime_contratacao: colaboradorDetalhes.regime_contratacao || '',
        estado_civil: colaboradorDetalhes.estado_civil || '',
        cpf: maskCPF(colaboradorDetalhes.cpf),
        rg: colaboradorDetalhes.rg || '',
        email: colaboradorDetalhes.email || '',
        telefone: colaboradorDetalhes.telefone ? maskPhone(colaboradorDetalhes.telefone) : '',
        matricula_esocial: colaboradorDetalhes.matricula_esocial || '',
        data_nascimento: colaboradorDetalhes.data_nascimento
          ? toInputDate(colaboradorDetalhes.data_nascimento)
          : '',
        data_admissao: colaboradorDetalhes.data_admissao
          ? toInputDate(colaboradorDetalhes.data_admissao)
          : '',
        data_demissao: colaboradorDetalhes.data_demissao
          ? toInputDate(colaboradorDetalhes.data_demissao)
          : '',
        sexo: colaboradorDetalhes.sexo || '',
        formacao_academica_id: colaboradorDetalhes.formacao_academica_id || undefined,
        cargo_id: colaboradorDetalhes.cargo?.id || colaboradorDetalhes.cargo_id || editingColaborador.cargo?.id,
        setor_id: colaboradorDetalhes.setor?.id || colaboradorDetalhes.setor_id || editingColaborador.setor?.id,
        cargo_nome: colaboradorDetalhes.cargo?.nome || colaboradorDetalhes.cargo_nome || editingColaborador.cargo?.nome,
        setor_nome: colaboradorDetalhes.setor?.nome || colaboradorDetalhes.setor_nome || editingColaborador.setor?.nome,
        ultimo_periodico: colaboradorDetalhes.ultimo_periodico ? toInputDate(colaboradorDetalhes.ultimo_periodico) : '',
        tempo_periodico: colaboradorDetalhes.tempo_periodico || undefined,
        tipo_id: colaboradorDetalhes.tipo_id || undefined,
      }));
    }
  }, [colaboradorDetalhes, editingColaborador]);

  const colaboradores = useMemo(() => response?.data || [], [response?.data]);
  const pagination = response?.meta || {
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  };
  const cargos = cargosData?.data || [];
  const setores = setoresData?.data || [];
  const formacoes = formacoesData || [];

  const createMutation = useMutation({
    mutationFn: (data: CreateColaboradorDto) => colaboradoresService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['colaboradores'] });
      setIsModalOpen(false);
      resetForm();
      toast.success('Colaborador criado com sucesso!');
    },
    onError: (error: unknown) => {
      const err = error as ApiError;
      console.error('❌ Erro ao criar colaborador:', err);
      toast.error(err?.response?.data?.message || 'Erro ao criar colaborador');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateColaboradorDto }) =>
      colaboradoresService.update(id, data),
    onSuccess: (updatedColaborador) => {
      // 1. Atualizar todas as listas de colaboradores no cache (Optimistic Update)
      queryClient.setQueriesData({ queryKey: ['colaboradores'] }, (oldData: { data?: Colaborador[] } | undefined) => {
        if (!oldData || !Array.isArray(oldData.data)) return oldData;
        return {
          ...oldData,
          data: oldData.data.map((item: Colaborador) =>
            item.id === updatedColaborador.id ? updatedColaborador : item
          ),
        };
      });

      // 2. Atualizar cache de detalhe se existir
      queryClient.setQueryData(['colaborador', updatedColaborador.id], updatedColaborador);

      // 3. Invalidar para garantir consistência (em background)
      queryClient.invalidateQueries({ queryKey: ['colaboradores'] });

      setIsModalOpen(false);
      setEditingColaborador(null);
      resetForm();
      toast.success('Colaborador atualizado com sucesso!');
    },
    onError: (error: unknown) => {
      const err = error as ApiError;
      console.error('❌ Erro ao atualizar colaborador:', err);
      toast.error(err?.response?.data?.message || 'Erro ao atualizar colaborador');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => colaboradoresService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['colaboradores'] });
      toast.success('Colaborador removido com sucesso!');
    },
    onError: (error: unknown) => {
      const err = error as ApiError;
      console.error('❌ Erro ao remover colaborador:', err);
      toast.error(err?.response?.data?.message || 'Erro ao remover colaborador');
    },
  });

  const resetForm = () => {
    // Calcular data mínima (14 anos atrás)
    const minDate = new Date();
    minDate.setFullYear(minDate.getFullYear() - 14);
    const minDateStr = format(minDate, 'yyyy-MM-dd');

    setFormData({
      id: undefined,
      nome: '',
      nome_social: '',
      pcd: false,
      enviar_esocial: true,
      regime_contratacao: '',
      estado_civil: '',
      cpf: '',
      rg: '',
      email: '',
      telefone: '',
      matricula_esocial: '',
      data_nascimento: minDateStr, // Abrir em 14 anos atrás
      data_admissao: '',
      data_demissao: '',
      sexo: '',
      formacao_academica_id: undefined,
      cargo_id: undefined,
      setor_id: undefined,
      cargo_nome: undefined,
      setor_nome: undefined,
      ultimo_periodico: '',
      tempo_periodico: undefined,
      tipo_id: undefined,
    });
    setEditingColaborador(null);
    setCreationStep('aviso_admissional');
  };

  const handleOpenCreate = () => {
    resetForm();
    setCreationStep('aviso_admissional');
    setIsModalOpen(true);
  };

  // Escutar evento para abrir modal de criação
  useEffect(() => {
    const handleOpenModal = () => {
      resetForm();
      setCreationStep('aviso_admissional');
      setIsModalOpen(true);
    };

    window.addEventListener('openColaboradorModal', handleOpenModal);

    return () => {
      window.removeEventListener('openColaboradorModal', handleOpenModal);
    };
  }, []);

  const handleOpenEdit = (colaborador: Colaborador) => {
    setEditingColaborador(colaborador);
    setCreationStep('formulario');
    // Pré-preenchimento imediato e instantâneo com os dados existentes do colaborador
    setFormData({
      id: colaborador.id,
      nome: colaborador.nome || '',
      nome_social: colaborador.nome_social || '',
      pcd: colaborador.pcd ?? false,
      enviar_esocial: colaborador.enviar_esocial ?? true,
      regime_contratacao: colaborador.regime_contratacao || '',
      estado_civil: colaborador.estado_civil || '',
      cpf: maskCPF(colaborador.cpf),
      rg: colaborador.rg || '',
      email: colaborador.email || '',
      telefone: colaborador.telefone ? maskPhone(colaborador.telefone) : '',
      matricula_esocial: colaborador.matricula_esocial || '',
      data_nascimento: colaborador.data_nascimento
        ? toInputDate(colaborador.data_nascimento)
        : '',
      data_admissao: colaborador.data_admissao
        ? toInputDate(colaborador.data_admissao)
        : '',
      data_demissao: colaborador.data_demissao
        ? toInputDate(colaborador.data_demissao)
        : '',
      sexo: colaborador.sexo || '',
      formacao_academica_id: colaborador.formacao_academica_id || undefined,
      cargo_id: colaborador.cargo?.id || colaborador.cargo_id || undefined,
      setor_id: colaborador.setor?.id || colaborador.setor_id || undefined,
      cargo_nome: colaborador.cargo?.nome || colaborador.cargo_nome || undefined,
      setor_nome: colaborador.setor?.nome || colaborador.setor_nome || undefined,
      ultimo_periodico: colaborador.ultimo_periodico ? toInputDate(colaborador.ultimo_periodico) : '',
      tempo_periodico: colaborador.tempo_periodico || undefined,
      tipo_id: colaborador.tipo_id || undefined,
    });
    setIsModalOpen(true);
  };

  const handleDelete = (id: number) => {
    setConfirmState({
      isOpen: true,
      title: 'Excluir Colaborador',
      message: 'Tem certeza que deseja excluir este colaborador?',
      onConfirm: () => deleteMutation.mutate(id)
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.nome || !formData.cpf) {
      showAlert('Erro', 'Preencha todos os campos obrigatórios', 'error');
      return;
    }

    if (!isValidCPF(formData.cpf)) {
      showAlert('Erro', 'O CPF informado é inválido. Verifique os dígitos e tente novamente.', 'error');
      return;
    }

    if (!formData.data_admissao) {
      showAlert('Erro', 'Data de admissão é estritamente obrigatória', 'error');
      return;
    }

    // Validar data de admissão não pode ser maior que data de demissão
    if (formData.data_admissao && formData.data_demissao) {
      const dataAdmissao = new Date(formData.data_admissao);
      const dataDemissao = new Date(formData.data_demissao);
      if (dataAdmissao > dataDemissao) {
        showAlert('Erro', 'Data de admissão não pode ser maior que data de demissão', 'error');
        return;
      }
    }

    // Validar data de nascimento: mínimo 14 anos atrás
    if (formData.data_nascimento) {
      const dataNascimento = new Date(formData.data_nascimento);
      const minDate = new Date();
      minDate.setFullYear(minDate.getFullYear() - 14);
      if (dataNascimento > minDate) {
        showAlert('Erro', 'Data de nascimento deve ser de pelo menos 14 anos atrás', 'error');
        return;
      }
    }

    const submitData: CreateColaboradorDto & {
      empresa_id?: number;
      cargo_nome?: string;
      setor_nome?: string;
    } = {
      ...formData,
      cpf: unmask(formData.cpf),
      telefone: formData.telefone ? unmask(formData.telefone) : undefined,
      data_demissao: formData.data_demissao || null,
      ultimo_periodico: formData.ultimo_periodico || null,
      tempo_periodico: formData.tempo_periodico || null,
      tipo_id: formData.tipo_id || null,
      formacao_academica_id: formData.formacao_academica_id || null,
    };

    // Remover campos que não devem ser enviados
    delete submitData.empresa_id; // API pega do token
    delete submitData.cargo_nome; // Componente trata criação
    delete submitData.setor_nome; // Componente trata criação

    if (editingColaborador) {
      // Garantir imutabilidade de cargo e setor na edição cadastral
      submitData.cargo_id = (editingColaborador.cargo?.id || editingColaborador.cargo_id) ?? undefined;
      submitData.setor_id = (editingColaborador.setor?.id || editingColaborador.setor_id) ?? undefined;
      updateMutation.mutate({ id: editingColaborador.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleToggleStatus = useCallback((id: number, currentStatus: boolean, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevenir abrir detalhes
    updateMutation.mutate({
      id,
      data: { enabled: !currentStatus }
    });
  }, [updateMutation]);

  const columns: Column<Colaborador>[] = useMemo(
    () => [
      {
        key: 'selection',
        label: (
          <div className="flex items-center justify-center">
            <input
              type="checkbox"
              checked={
                colaboradores.length > 0 &&
                selectedColaboradoresIds.length === colaboradores.length
              }
              onChange={() => {
                if (selectedColaboradoresIds.length === colaboradores.length) {
                  setSelectedColaboradoresIds([]);
                } else {
                  setSelectedColaboradoresIds(colaboradores.map((c) => c.id));
                }
              }}
              className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
              title="Selecionar todos os colaboradores da página"
            />
          </div>
        ),
        sortable: false,
        render: (item) => (
          <div className="flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={selectedColaboradoresIds.includes(item.id)}
              onChange={() => {
                setSelectedColaboradoresIds((prev) =>
                  prev.includes(item.id) ? prev.filter((id) => id !== item.id) : [...prev, item.id],
                );
              }}
              className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
            />
          </div>
        ),
      },
      {
        key: 'nome',
        label: 'Nome',
        sortable: true,
        render: (item) => (
          <div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-semibold text-gray-900">{item.nome}</span>
              {item.recem_transferido && (
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200"
                  title={`Recém transferido para esta unidade em ${item.transferido_em ? formatDateBR(item.transferido_em) : 'data recente'}${item.empresa_transferencia_origem ? ` (Origem: ${item.empresa_transferencia_origem.nome || item.empresa_transferencia_origem.razao_social})` : ''}`}
                >
                  <ArrowsRightLeftIcon className="w-3 h-3 text-indigo-600" />
                  Recém Transferido
                </span>
              )}
            </div>
            {item.nome_social && item.nome_social.trim() !== item.nome.trim() && (
              <div className="text-xs text-gray-400">Nome social: {item.nome_social}</div>
            )}
          </div>
        ),
      },
      {
        key: 'cpf',
        label: 'CPF',
        render: (item) => formatCPF(item.cpf),
        sortable: true,
      },
      {
        key: 'email',
        label: 'Email',
        sortable: true,
      },
      {
        key: 'telefone',
        label: 'Telefone',
        render: (item) => formatPhone(item.telefone),
        sortable: false,
      },
      {
        key: 'cargo',
        label: 'Cargo',
        render: (item) => item.cargo?.nome || 'N/A',
        sortable: false,
      },
      {
        key: 'setor',
        label: 'Setor',
        render: (item) => item.setor?.nome || 'N/A',
        sortable: false,
      },
      {
        key: 'data_admissao',
        label: 'Admissão',
        render: (item) => item.data_admissao
          ? formatDateBR(item.data_admissao)
          : 'N/A',
        sortable: false,
      },
      {
        key: 'enabled',
        label: 'Status',
        render: (item) => (
          <div onClick={(e) => e.stopPropagation()}>
            <Toggle
              checked={item.enabled ?? true}
              onChange={() => { }} // Controlado pelo onClick do wrapper pra stopPropagation
              onClick={(e) => handleToggleStatus(item.id, item.enabled ?? true, e)}
            />
          </div>
        ),
        sortable: false,
      },
    ],
    [colaboradores, selectedColaboradoresIds, handleToggleStatus],
  );

  return (
    <PageContainer>
      <PageHeader
        title="Colaboradores"
        description="Gerencie seus colaboradores"
        actions={
          <button
            onClick={handleOpenCreate}
            className="bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors font-medium text-sm shadow-sm"
          >
            <PlusIcon className="w-5 h-5" />
            Novo Colaborador
          </button>
        }
      />

        {/* Banner de Autosserviço de Manutenção de Quadro */}
        {/* Banner de Autosserviço e Atualização de Quadro (Design Claro e Limpo) */}
        <div className="mb-6 bg-white text-gray-800 rounded-xl p-5 shadow-xs flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border border-gray-200">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                Autosserviço & Quadro
              </span>
              <span className="text-xs text-gray-500">Manutenção Rápida de Pessoas</span>
            </div>
            <h3 className="text-base font-bold text-gray-900 tracking-tight">
              Precisa atualizar quem entra ou sai da empresa?
            </h3>
            <p className="text-xs text-gray-500 max-w-2xl leading-relaxed">
              Resolva divergências de colaboradores com total autonomia. Inclusões retroativas diretas
              e desligamentos sem demissional com justificativa arquivada para auditoria.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2.5 flex-shrink-0 w-full md:w-auto">
            <button
              onClick={() => setIsRetroModalOpen(true)}
              className="flex-1 md:flex-none px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg shadow-xs flex items-center justify-center gap-2 transition-colors cursor-pointer"
            >
              <UserPlusIcon className="w-4 h-4" />
              Sentiu falta de alguém?
            </button>
            <button
              onClick={() => handleOpenDesligamento()}
              className="flex-1 md:flex-none px-4 py-2.5 bg-white hover:bg-rose-50 text-rose-700 hover:text-rose-800 border border-rose-200 hover:border-rose-300 text-xs font-bold rounded-lg shadow-xs flex items-center justify-center gap-2 transition-colors cursor-pointer"
            >
              <UserMinusIcon className="w-4 h-4 text-rose-600" />
              Alguém não deveria estar aqui?
            </button>
            {hasGrupoEconomicoFiliais && (
              <button
                onClick={() => {
                  if (selectedColaboradoresIds.length > 0) {
                    const selected = colaboradores.filter((c) => selectedColaboradoresIds.includes(c.id));
                    setTransferTargetColaboradores(selected);
                  } else {
                    setTransferTargetColaboradores(colaboradores);
                  }
                  setIsTransferModalOpen(true);
                }}
                className="flex-1 md:flex-none px-4 py-2.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 hover:text-indigo-800 border border-indigo-200 hover:border-indigo-300 text-xs font-bold rounded-lg shadow-xs flex items-center justify-center gap-2 transition-colors cursor-pointer"
              >
                <ArrowsRightLeftIcon className="w-4 h-4 text-indigo-600" />
                Transferir de Filial
              </button>
            )}
          </div>
        </div>

        <DataTable
          data={colaboradores}
          columns={columns}
          loading={isLoading}
          getId={(item) => item.id}
          emptyMessage="Nenhum colaborador encontrado."
          pagination={pagination}
          onPageChange={setPage}
          onLimitChange={setLimit}
          onFiltersChange={setFilters}
          actions={(item) => (
            <div className="flex gap-2">
              <button
                onClick={() => navigate(`/colaboradores/${item.id}/ficha`)}
                className="btn-secondary flex items-center gap-2 text-sm"
              >
                <EyeIcon className="h-4 w-4" />
                Detalhes
              </button>
              <button
                onClick={() => handleOpenEdit(item)}
                className="btn-secondary flex items-center gap-2 text-sm"
              >
                <PencilIcon className="h-4 w-4" />
                Editar
              </button>
              {hasGrupoEconomicoFiliais && (
                <button
                  onClick={() => {
                    setTransferTargetColaboradores([item]);
                    setIsTransferModalOpen(true);
                  }}
                  title="Transferir para outra unidade do Grupo Econômico"
                  className="bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 px-2.5 py-1 rounded flex items-center gap-1.5 text-sm transition-colors cursor-pointer"
                >
                  <ArrowsRightLeftIcon className="h-4 w-4 text-indigo-600" />
                  Transferir
                </button>
              )}
              <button
                onClick={() => handleOpenDesligamento(item)}
                title="Registrar Desligamento sem Demissional"
                className="bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 px-2.5 py-1 rounded flex items-center gap-1.5 text-sm transition-colors"
              >
                <UserMinusIcon className="h-4 w-4" />
                Desligar
              </button>
              <button
                onClick={() => handleDelete(item.id)}
                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded flex items-center gap-2 text-sm transition-colors"
              >
                <TrashIcon className="h-4 w-4" />
                Excluir
              </button>
            </div>
          )}
        />

        {/* Barra Flutuante de Ação em Lote */}
        {selectedColaboradoresIds.length > 0 && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-gray-900/95 backdrop-blur-md text-white px-6 py-3.5 rounded-2xl shadow-2xl flex items-center gap-4 animate-in fade-in slide-in-from-bottom-4 duration-200 border border-gray-700">
            <div className="flex items-center gap-2.5">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
              </span>
              <span className="text-sm font-semibold">
                <strong className="text-emerald-400 font-bold">{selectedColaboradoresIds.length}</strong> colaborador(es) selecionado(s)
              </span>
            </div>
            <div className="h-4 w-px bg-gray-700" />
            {hasGrupoEconomicoFiliais && (
              <button
                type="button"
                onClick={() => {
                  const selected = colaboradores.filter((c) => selectedColaboradoresIds.includes(c.id));
                  setTransferTargetColaboradores(selected);
                  setIsTransferModalOpen(true);
                }}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-xl shadow-sm flex items-center gap-2 transition-colors cursor-pointer"
              >
                <ArrowsRightLeftIcon className="w-4 h-4" />
                <span>Transferir para Outra Filial</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => setSelectedColaboradoresIds([])}
              className="text-xs text-gray-400 hover:text-white transition-colors cursor-pointer px-2 py-1"
            >
              Desmarcar
            </button>
          </div>
        )}

      {/* Modal de criação/edição */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div
            className={`bg-white rounded-2xl shadow-2xl w-full max-h-[90vh] overflow-y-auto transition-all duration-300 border border-gray-100 ${
              !editingColaborador && creationStep === 'aviso_admissional'
                ? 'max-w-xl'
                : 'md:max-w-[95vw]'
            }`}
          >
            {/* ETAPA 1: AVISO E DIRECIONAMENTO PARA EXAME ADMISSIONAL (Apenas na criação) */}
            {!editingColaborador && creationStep === 'aviso_admissional' ? (
              <div>
                {/* Header do Aviso */}
                <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex justify-between items-center z-10 rounded-t-2xl">
                  <div className="flex items-center gap-2.5">
                    <span className="w-8 h-8 rounded-lg bg-emerald-50 border border-emerald-200/80 flex items-center justify-center text-emerald-600">
                      <SparklesIcon className="w-4 h-4" />
                    </span>
                    <div>
                      <h2 className="text-lg font-bold text-gray-900 leading-tight">
                        Novo Colaborador
                      </h2>
                      <p className="text-xs text-gray-500">
                        Orientações rápidas antes de iniciar
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setIsModalOpen(false);
                      resetForm();
                    }}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100 cursor-pointer"
                    type="button"
                    title="Fechar"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                {/* Conteúdo com os dois caminhos claros */}
                <div className="p-6 sm:p-7 space-y-5">
                  {/* Bloco de Mensagem Principal */}
                  <div className="bg-gradient-to-br from-amber-50/90 via-amber-50/40 to-orange-50/20 border border-amber-200/80 rounded-2xl p-4 sm:p-5 relative">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl bg-amber-500/15 border border-amber-300/60 flex items-center justify-center text-amber-700 flex-shrink-0 mt-0.5">
                        <InformationCircleIcon className="w-5 h-5" />
                      </div>
                      <div className="space-y-1.5">
                        <h3 className="text-base font-bold text-gray-900">
                          Vai realizar um Exame Admissional?
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                          Para novos colaboradores que irão realizar <strong>Exame Admissional</strong>, <strong>não é necessário realizar o cadastro prévio por aqui</strong>.
                        </p>
                        <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                          Todos os dados do profissional e do contrato são informados diretamente no fluxo de agendamento de exames, gerando o cadastro e a vinculação automaticamente.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Opções de Ação */}
                  <div className="space-y-3 pt-1">
                    {/* Opção 1: Agendamento de Admissional (Principal / Recomendada) */}
                    <button
                      type="button"
                      onClick={() => {
                        setIsModalOpen(false);
                        resetForm();
                        navigate('/agendamento/cadastro?tab=individual');
                      }}
                      className="w-full text-left group bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-500 hover:to-teal-600 text-white rounded-xl p-4 sm:p-5 shadow-sm hover:shadow-md transition-all cursor-pointer flex items-center justify-between gap-4 border border-emerald-500/20"
                    >
                      <div className="flex items-center gap-3.5">
                        <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center text-white flex-shrink-0 group-hover:scale-105 transition-transform">
                          <CalendarDaysIcon className="w-6 h-6" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm sm:text-base font-bold text-white">
                              Agendar Exame Admissional
                            </span>
                            <span className="text-[10px] font-extrabold uppercase tracking-wider bg-white/25 text-white px-2 py-0.5 rounded-full">
                              Recomendado
                            </span>
                          </div>
                          <p className="text-xs text-emerald-50 mt-0.5 leading-snug">
                            Informar dados do candidato e agendar a clínica em uma única etapa
                          </p>
                        </div>
                      </div>
                      <ArrowRightIcon className="w-5 h-5 text-white group-hover:translate-x-1 transition-transform flex-shrink-0" />
                    </button>

                    {/* Opção 2: Não é para admissional, continuar cadastro */}
                    <button
                      type="button"
                      onClick={() => setCreationStep('formulario')}
                      className="w-full text-left group bg-white hover:bg-gray-50 border border-gray-200 hover:border-gray-300 rounded-xl p-4 sm:p-5 transition-all cursor-pointer flex items-center justify-between gap-4 shadow-2xs"
                    >
                      <div className="flex items-center gap-3.5">
                        <div className="w-10 h-10 rounded-xl bg-gray-100 group-hover:bg-gray-200/80 flex items-center justify-center text-gray-700 flex-shrink-0 transition-colors">
                          <UserPlusIcon className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="text-sm sm:text-base font-bold text-gray-900 group-hover:text-gray-950">
                            Não é para admissional, fazer cadastro
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5 leading-snug">
                            Para colaboradores já contratados, regularização de base ou histórico
                          </p>
                        </div>
                      </div>
                      <span className="text-xs font-bold text-gray-500 group-hover:text-gray-900 group-hover:translate-x-0.5 transition-all flex-shrink-0">
                        Continuar →
                      </span>
                    </button>
                  </div>

                  {/* Rodapé de fechamento */}
                  <div className="pt-2 border-t border-gray-100 flex items-center justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setIsModalOpen(false);
                        resetForm();
                      }}
                      className="px-4 py-2 text-xs font-semibold text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10 rounded-t-2xl">
                  <div className="flex items-center gap-3">
                    {!editingColaborador && (
                      <button
                        type="button"
                        onClick={() => setCreationStep('aviso_admissional')}
                        className="text-xs font-semibold text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 px-2.5 py-1.5 rounded-lg transition-colors flex items-center gap-1 cursor-pointer"
                        title="Voltar às orientações sobre admissional"
                      >
                        <span>← Voltar</span>
                      </button>
                    )}
                    <h2 className="text-xl font-bold text-gray-900">
                      {editingColaborador ? 'Editar Colaborador' : 'Novo Colaborador'}
                    </h2>
                  </div>
                  <button
                    onClick={() => {
                      setIsModalOpen(false);
                      resetForm();
                    }}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100 cursor-pointer"
                    type="button"
                    title="Fechar"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6">
                  {/* Banner de Lembrete no topo do formulário caso seja criação nova */}
                  {!editingColaborador && (
                    <div className="mb-5 bg-gradient-to-r from-amber-50 to-emerald-50/50 border border-amber-200/80 rounded-xl p-3.5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs">
                      <div className="flex items-center gap-2.5 text-amber-950">
                        <InformationCircleIcon className="w-5 h-5 text-amber-600 flex-shrink-0" />
                        <span>
                          <strong>Lembrete:</strong> Se o objetivo for agendar um <strong>Exame Admissional</strong>, não é necessário preencher este cadastro prévio.
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setIsModalOpen(false);
                          resetForm();
                          navigate('/agendamento/cadastro?tab=individual');
                        }}
                        className="inline-flex items-center gap-1 font-bold text-emerald-700 hover:text-emerald-900 underline whitespace-nowrap cursor-pointer flex-shrink-0"
                      >
                        <span>Ir para Agendamento</span>
                        <ArrowRightIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}

                  <div className="mb-6">
                    <ColaboradorForm
                      formData={formData}
                      onChange={setFormData}
                      tiposContrato={tiposContrato}
                      formacoes={formacoes}
                      setores={setores}
                      cargos={cargos}
                      isEditing={!!editingColaborador}
                      isLoading={createMutation.isPending || updateMutation.isPending || isLoadingDetalhes}
                      onSetoresChange={(newSetores) => {
                        queryClient.setQueryData(['setores'], (oldData: { data?: Setor[] } | undefined) => {
                          if (!oldData) return { data: newSetores };
                          return { ...oldData, data: newSetores };
                        });
                      }}
                      onCargosChange={(newCargos) => {
                        queryClient.setQueryData(['cargos'], (oldData: { data?: Cargo[] } | undefined) => {
                          if (!oldData) return { data: newCargos };
                          return { ...oldData, data: newCargos };
                        });
                      }}
                    />
                  </div>

                  <div className="flex gap-3 pt-4 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => {
                        setIsModalOpen(false);
                        resetForm();
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                      disabled={createMutation.isPending || updateMutation.isPending || isLoadingDetalhes}
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={createMutation.isPending || updateMutation.isPending || isLoadingDetalhes}
                      className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {createMutation.isPending || updateMutation.isPending || isLoadingDetalhes
                        ? 'Salvando...'
                        : editingColaborador
                          ? 'Atualizar'
                          : 'Salvar'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      )}

      <ToastContainer toasts={toast.toasts} onRemove={toast.removeToast} />

      <AlertDialog
        isOpen={alertState.isOpen}
        onClose={() => setAlertState(prev => ({ ...prev, isOpen: false }))}
        title={alertState.title}
        message={alertState.message}
        variant={alertState.variant}
      />

      <ConfirmDialog
        isOpen={confirmState.isOpen}
        onClose={() => setConfirmState(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
      />

      {/* Modais de Autosserviço de Manutenção de Quadro */}
      {empresa?.id && (
        <>
          <ModalAdmissaoRetroativa
            isOpen={isRetroModalOpen}
            onClose={() => setIsRetroModalOpen(false)}
            empresaId={empresa.id}
            cargos={cargos}
            setores={setores}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['colaboradores'] });
              navigate('/organizacao?tab=movimentacoes');
            }}
          />

          <ModalDesligamentoSemDemissional
            isOpen={isDesligamentoModalOpen}
            onClose={() => {
              setIsDesligamentoModalOpen(false);
              setDesligamentoTargetColab(null);
            }}
            empresaId={empresa.id}
            colaborador={desligamentoTargetColab}
            colaboradoresAtivos={colaboradores.filter((c) => c.enabled !== false)}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['colaboradores'] });
              navigate('/organizacao?tab=movimentacoes');
            }}
          />

          <TransferirColaboradoresModal
            isOpen={isTransferModalOpen}
            onClose={() => {
              setIsTransferModalOpen(false);
              setTransferTargetColaboradores([]);
            }}
            empresaOrigemId={empresa.id}
            empresaOrigemNome={empresa.nome}
            empresaOrigemCnpj={empresa.cnpj}
            colaboradores={transferTargetColaboradores}
            filiaisDisponiveis={filiaisDisponiveis}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['colaboradores'] });
              setSelectedColaboradoresIds([]);
            }}
          />
        </>
      )}
    </PageContainer>
  );
}
